import React, { useState, useEffect, useMemo } from "react";
import {
    Box,
    Grid,
    Paper,
    Typography,
    Button,
    Stack,
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Card,
    CardContent,
    InputLabel,
} from "@mui/material";
import DatePickerComponent from "../../Common/DatePicker";
import Select from "../../Common/Select";
import MultipleSelectJson from "../../Common/MultipleSelectJson";
import { Clear as ClearIcon } from "@mui/icons-material";

import { useGenerateWarrantyReportQuery } from "../../../store/reports/reportsApis";
import { useGetRegionsDropdownQuery } from "../../../store/masters/mastersApis";
import { useGetProductsDropdownQuery } from "../../../store/masters/mastersApis";

const WarrantyReport = ({
    reportType,
    filters,
    onFilterUpdate,
    onFilterReset,
    onExport,
    onRefresh,
    onReportDataUpdate,
}) => {
    const [localFilters, setLocalFilters] = useState(filters);

    // Update local filters when props change
    useEffect(() => {
        setLocalFilters(filters);
    }, [filters]);

    // Fetch dropdown data
    const { data: regionsData } = useGetRegionsDropdownQuery();
    const { data: productsData } = useGetProductsDropdownQuery();

    // Prepare filters for API - extract IDs from objects
    const prepareFiltersForAPI = (filters) => {
        const apiFilters = { ...filters };

        // Extract IDs from array of objects
        if (Array.isArray(apiFilters.region_ids)) {
            apiFilters.region_ids = apiFilters.region_ids.map((item) => item.value || item);
        }
        if (Array.isArray(apiFilters.product_ids)) {
            apiFilters.product_ids = apiFilters.product_ids.map((item) => item.value || item);
        }

        return apiFilters;
    };

    // Generate report query - real-time filtering with prepared filters
    const {
        data: reportData,
        isLoading,
        error,
        refetch,
    } = useGenerateWarrantyReportQuery(prepareFiltersForAPI(localFilters));

    // Update parent with report data whenever it changes
    useEffect(() => {
        if (reportData && onReportDataUpdate) {
            onReportDataUpdate("warranty", reportData);
        }
    }, [reportData, onReportDataUpdate]);

    // Handle filter changes - store complete objects
    const handleFilterChange = (field, value) => {
        const newFilters = { ...localFilters, [field]: value };
        setLocalFilters(newFilters);
        onFilterUpdate(newFilters);
    };

    // Handle reset
    const handleReset = () => {
        const resetFilters = {
            start_date: null,
            end_date: null,
            region_ids: [],
            product_ids: [],
            format: "json",
        };
        setLocalFilters(resetFilters);
        onFilterReset();
    };

    return (
        <Box>
            {/* Filters */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                    Filters
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <DatePickerComponent
                            label="Start Date"
                            value={localFilters.start_date}
                            onChange={(date) => handleFilterChange("start_date", date)}
                            name="start_date"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <DatePickerComponent
                            label="End Date"
                            value={localFilters.end_date}
                            onChange={(date) => handleFilterChange("end_date", date)}
                            name="end_date"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "6px" }}>
                            <InputLabel sx={{ fontSize: "14px", color: "#454545", fontWeight: "500" }} shrink={false}>
                                Regions
                            </InputLabel>
                            <MultipleSelectJson
                                id="region-select"
                                value={localFilters.region_ids || []}
                                onChange={(value) => handleFilterChange("region_ids", value)}
                                name="region_ids"
                                options={regionsData || []}
                                size="small"
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "6px" }}>
                            <InputLabel sx={{ fontSize: "14px", color: "#454545", fontWeight: "500" }} shrink={false}>
                                Products
                            </InputLabel>
                            <MultipleSelectJson
                                id="product-select"
                                value={localFilters.product_ids || []}
                                onChange={(value) => handleFilterChange("product_ids", value)}
                                name="product_ids"
                                options={productsData || []}
                                size="small"
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Select
                            label="Format"
                            value={localFilters.format || "json"}
                            onChange={(e) => handleFilterChange("format", e.target.value)}
                            name="format"
                            options={[
                                { value: "json", label: "JSON" },
                                { value: "csv", label: "CSV" },
                                { value: "excel", label: "Excel" },
                            ]}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Stack direction="row" spacing={1}>
                            <Button variant="outlined" startIcon={<ClearIcon />} onClick={handleReset} size="small">
                                Reset
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            {/* Loading State */}
            {isLoading && (
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                </Box>
            )}

            {/* Error State */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error?.data?.message || "Failed to generate warranty report"}
                </Alert>
            )}

            {/* Report Data */}
            {reportData && !isLoading && (
                <Box>
                    {/* Summary Cards */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>
                                        Total Registrations
                                    </Typography>
                                    <Typography variant="h4">{reportData.summary?.total_registrations || 0}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>
                                        Unique Products
                                    </Typography>
                                    <Typography variant="h4">
                                        {Object.keys(reportData.summary?.product_breakdown || {}).length}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>
                                        Unique Dealers
                                    </Typography>
                                    <Typography variant="h4">
                                        {Object.keys(reportData.summary?.dealer_breakdown || {}).length}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>
                                        States Covered
                                    </Typography>
                                    <Typography variant="h4">
                                        {Object.keys(reportData.summary?.state_breakdown || {}).length}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Breakdown Charts */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="h6" gutterBottom>
                                    Dealer Breakdown
                                </Typography>
                                {reportData.summary?.dealer_breakdown && (
                                    <Box>
                                        {Object.entries(reportData.summary.dealer_breakdown).map(([dealer, count]) => (
                                            <Box key={dealer} display="flex" justifyContent="space-between" mb={1}>
                                                <Typography variant="body2" textTransform="capitalize">
                                                    {dealer}
                                                </Typography>
                                                <Chip label={count} size="small" color="secondary" />
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="h6" gutterBottom>
                                    Product Breakdown
                                </Typography>
                                {reportData.summary?.product_breakdown && (
                                    <Box>
                                        {Object.entries(reportData.summary.product_breakdown).map(
                                            ([product, count]) => (
                                                <Box key={product} display="flex" justifyContent="space-between" mb={1}>
                                                    <Typography variant="body2" textTransform="capitalize">
                                                        {product}
                                                    </Typography>
                                                    <Chip label={count} size="small" color="primary" />
                                                </Box>
                                            ),
                                        )}
                                    </Box>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* Warranty Registrations Table */}
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Warranty Registrations Details ({reportData.warranty_registrations?.length || 0}{" "}
                            registrations)
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Registration ID</TableCell>
                                        <TableCell>Customer</TableCell>
                                        <TableCell>Product</TableCell>
                                        <TableCell>Dealer</TableCell>
                                        <TableCell>Region</TableCell>
                                        <TableCell>Purchase Date</TableCell>
                                        <TableCell>Created Date</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reportData.warranty_registrations?.map((registration) => (
                                        <TableRow key={registration.id}>
                                            <TableCell>{registration.registration_number || registration.id}</TableCell>
                                            <TableCell>
                                                {registration.customer?.name}
                                                <br />
                                                <Typography variant="caption" color="text.secondary">
                                                    {registration.customer?.mobile}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{registration.product?.name}</TableCell>
                                            <TableCell>
                                                {registration.dealer_name}
                                                <br />
                                                <Typography variant="caption" color="text.secondary">
                                                    {registration.dealer_state}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{registration.customer?.region?.name}</TableCell>
                                            <TableCell>
                                                {new Date(registration.purchase_date).toLocaleDateString()}
                                            </TableCell>
                                            <TableCell>
                                                {new Date(registration.created_at).toLocaleDateString()}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Box>
            )}

            {/* No Data State */}
            {!reportData && !isLoading && !error && (
                <Alert severity="info">No warranty registrations found for the selected filters.</Alert>
            )}
        </Box>
    );
};

export default WarrantyReport;
